import React, { useEffect } from "react";
import { DisconnectionPropertyDetails } from "../../../../types/DisconnectionPropertyDetails";
import { Flex } from "../../../Flex";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { FormikCounter } from "../../../FormikCounter";
import { FormInputErrorMessage } from "../../../FormInputErrorMessage";
import { DisconnectionPropertyDetailsMeter } from "../DisconnectionPropertyDetailsMeter";
import styled from "styled-components";
import { Box } from "../../../Box";
import { Span } from "../../../Span";
import { StreetNumberAndName } from "../../../StreetNumberAndName";
import { EMPTY_METER_VALUES } from "../../../../constants/DisconnectionMeterValues";

interface Props {
  id: string;
  propertyDetails: DisconnectionPropertyDetails;
  validateField: (field: string) => void;
  addressCaptureEnabled: boolean;
}

const MetersWrapper = styled(Box)`
  display: inline-block;
  background-color: ${({ theme }) => theme.colors.neutrals.ln10};
  border: 1px solid;
  border-color: ${({ theme }) => theme.colors.neutrals.ln30};
`;

export const DisconnectionPropertyDetailsMeterForm: React.FC<Props> = ({
  id,
  propertyDetails,
  validateField,
  addressCaptureEnabled,
}) => {
  const { setFieldValue } = useFormikContext();
  const meterCount = propertyDetails.meterInfo.meterCount;
  const meters = propertyDetails.meterInfo.meters;

  useEffect(() => {
    if (meters.length === meterCount) {
      return;
    }
    const newMeters = [...meters];
    while (meterCount > newMeters.length) {
      newMeters.push(EMPTY_METER_VALUES);
    }

    while (meterCount < newMeters.length) {
      newMeters.pop();
    }

    setFieldValue("propertyDetails.meterInfo.meters", newMeters);
  }, [meterCount, meters, setFieldValue]);

  return (
    <Flex
      id={id}
      flexDirection="column"
      flexGrow={0.3}
      flexBasis={0}
      mt={4}
      mb={[3, 3, 3, 0]}
    >
      <Flex pl={2} pr={2}>
        <Span
          display="inline-block"
          fontSize={2}
          lineHeight={2}
          fontWeight="medium"
        >
          Number of electricity meters to be disconnected
        </Span>
      </Flex>
      <Box maxWidth={"184px"} data-testid={"meter-count"}>
        <Field
          id="propertyDetails.meterInfo.meterCount"
          name="propertyDetails.meterInfo.meterCount"
          min={-1}
          max={5}
          value={propertyDetails.meterInfo.meterCount}
          component={FormikCounter}
        />
        <ErrorMessage
          name="propertyDetails.meterInfo.meterCount"
          className="field-error"
          component={FormInputErrorMessage}
        />
      </Box>

      {propertyDetails.meterInfo.meters.length > 0 && (
        <MetersWrapper p={4} borderRadius="radio" mt={4}>
          <DisconnectionPropertyDetailsMeter
            meters={propertyDetails.meterInfo.meters}
            meterSerialOptional={!addressCaptureEnabled}
          />

          {addressCaptureEnabled && (
            <StreetNumberAndName
              fieldIdPrefix={"propertyDetails.meterInfo"}
              fieldNamePrefix={"propertyDetails.meterInfo"}
              siteAddressInformation={
                propertyDetails.meterInfo.addressInformation
              }
              validateField={validateField}
              showUnit={true}
            />
          )}
        </MetersWrapper>
      )}
    </Flex>
  );
};
